'use client';

import { startTransition, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';
import { Button } from '@/lib/components/ui/button';
import { useTranslation } from '@/lib/Contexts/TranslationsContext';
import { isStaleServerActionError } from '@/lib/utils/handle-error';
import { cn } from '@/lib/utils/helpers/cn';

/**
 * The body of every `error.tsx` under app/[locale] - one implementation so
 * no route boundary prints the raw error to the user (it goes to Sentry
 * only). A Server Action from a previous deployment (the tab was opened
 * before a deploy) gets an "app updated, reload" screen with a full reload,
 * since retrying in place would call the same unknown action again.
 */
export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const dictionary = useTranslation();
  const router = useRouter();
  const isStaleAction = isStaleServerActionError(error);

  function handleReset() {
    startTransition(() => {
      reset();
      router.refresh();
    });
  }

  useEffect(() => {
    Sentry.captureException(error);
    console.error(error);
  }, [error]);

  return (
    <div
      role="alert"
      className={cn(
        'flex h-full items-center justify-center rounded',
        isStaleAction
          ? 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
          : 'bg-red-100 text-red-600'
      )}
    >
      <div className="rounded text-center">
        <h1 className="m-auto my-4 text-3xl">
          {isStaleAction
            ? dictionary.general.AppUpdatedPleaseRefresh
            : dictionary.general.SomethingWentWrong}
        </h1>
        <div className="flex items-center justify-center gap-10">
          <Button
            className="mt-4 p-2"
            onClick={
              isStaleAction ? () => window.location.reload() : handleReset
            }
          >
            {isStaleAction
              ? dictionary.general.Reload
              : dictionary.general.TryAgain}
          </Button>
        </div>
      </div>
    </div>
  );
}
